import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Dashboard from './pages/Dashboard'
import Tenants from './pages/Tenants'
import TenantDetail from './pages/TenantDetail'
import CreateTenant from './pages/CreateTenant'
import Users from './pages/Users'
import CreateUser from './pages/CreateUser'
import Settings from './pages/Settings'
import AdvancedSettings from './pages/AdvancedSettings'
import ApiKeys from './pages/ApiKeys'
import Login from './pages/Login'
import Layout from './components/Layout'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Tasks from './pages/Tasks'
import CreateTask from './pages/CreateTask'
import CreateGlobalTask from './pages/CreateGlobalTask'
import GlobalTaskDetail from './pages/GlobalTaskDetail'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { googleClientId, isGoogleOAuthConfigured } from './lib/google-oauth'
import SystemEmbeddings from './pages/SystemEmbeddings'
import RolePermissions from './pages/RolePermissions'
import Lars from './pages/labs/Lars'
import Sdl from './pages/labs/Sdl'

const queryClient = new QueryClient()

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  const { isAuthenticated } = useAuth()

  const routes = (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <Login />
      } />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/:id" element={<TenantDetail />} />
        <Route path="tenants/create" element={<CreateTenant />} />
        <Route path="users" element={<Users />} />
        <Route path="users/create" element={<CreateUser />} />
        <Route path="settings" element={<Settings />} />
        <Route path="system-embeddings" element={<SystemEmbeddings />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="tasks/create" element={<CreateTask />} />
        <Route path="tasks/global/create" element={<CreateGlobalTask />} />
        <Route path="tasks/global/:id" element={<GlobalTaskDetail />} />
        <Route path="advanced-settings" element={<AdvancedSettings />} />
        <Route path="role-permissions" element={<RolePermissions />} />
        <Route path="keys" element={<ApiKeys />} />
        <Route path="labs" element={<Navigate to="/labs/lars" replace />} />
        <Route path="labs/lars" element={<Lars />} />
        <Route path="labs/sdl" element={<Sdl />} />
      </Route>
    </Routes>
  )

  if (!isGoogleOAuthConfigured) {
    return routes
  }

  return <GoogleOAuthProvider clientId={googleClientId}>{routes}</GoogleOAuthProvider>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
